/**
 * TeamFormModal - Create/edit team form
 *
 * Fields: name, type, description, leader picker, site picker, color, notes
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { X, Users } from 'lucide-react';
import { teamAPI, driverAPI, workingSiteAPI } from '../../api';

const TEAM_TYPES = ['chantier', 'maintenance', 'transport', 'mixed', 'other'];

const DEFAULT_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
];

export default function TeamFormModal({ team, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!team;

  const [form, setForm] = useState({
    name: '',
    team_type: 'mixed',
    description: '',
    team_lead_id: '',
    working_site_id: '',
    color: '#3B82F6',
    max_members: 20,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (team) {
      setForm({
        name: team.name || '',
        team_type: team.team_type || 'mixed',
        description: team.description || '',
        team_lead_id: team.team_lead_id || '',
        working_site_id: team.working_site_id || '',
        color: team.color || '#3B82F6',
        max_members: team.max_members || 20,
        notes: team.notes || '',
      });
    }
  }, [team]);

  const { data: driversData } = useQuery({
    queryKey: ['drivers-for-team'],
    queryFn: () => driverAPI.getAll(),
    staleTime: 60000,
  });

  const { data: sitesData } = useQuery({
    queryKey: ['sites-for-team'],
    queryFn: () => workingSiteAPI.getAll(),
    staleTime: 60000,
  });

  const drivers = driversData?.data || [];
  const sites = sitesData?.data || [];

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError(t('teams.errors.nameRequired'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        ...form,
        team_lead_id: form.team_lead_id ? parseInt(form.team_lead_id) : null,
        working_site_id: form.working_site_id ? parseInt(form.working_site_id) : null,
        max_members: parseInt(form.max_members) || 20,
      };

      if (isEdit) {
        await teamAPI.update(team.id, payload);
      } else {
        await teamAPI.create(payload);
      }
      onSaved?.();
    } catch (err) {
      setError(err.message || t('teams.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-gray-900">
              {isEdit ? t('teams.form.editTitle') : t('teams.form.createTitle')}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('teams.form.name')}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('teams.form.namePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
            />
          </div>

          {/* Type + Max members */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('teams.form.type')}</label>
              <select
                value={form.team_type}
                onChange={(e) => handleChange('team_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                {TEAM_TYPES.map(code => (
                  <option key={code} value={code}>{t(`teams.type.${code}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('teams.form.maxMembers')}</label>
              <input
                type="number"
                min="1"
                max="100"
                value={form.max_members}
                onChange={(e) => handleChange('max_members', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('teams.form.description')}</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
              placeholder={t('teams.form.descriptionPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          {/* Leader */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('teams.form.leader')}</label>
            <select
              value={form.team_lead_id}
              onChange={(e) => handleChange('team_lead_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('teams.form.none')}</option>
              {drivers.filter(d => d.status === 'active').map(d => (
                <option key={d.id} value={d.id}>
                  {d.first_name} {d.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Site */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('teams.form.site')}</label>
            <select
              value={form.working_site_id}
              onChange={(e) => handleChange('working_site_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('teams.form.none')}</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>{s.site_name} — {s.city || s.site_code}</option>
              ))}
            </select>
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('teams.form.color')}</label>
            <div className="flex items-center gap-2">
              {DEFAULT_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => handleChange('color', c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                    form.color === c ? 'border-gray-800 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <input
                type="color"
                value={form.color}
                onChange={(e) => handleChange('color', e.target.value)}
                className="w-7 h-7 rounded cursor-pointer"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('teams.form.notes')}</label>
            <textarea
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={2}
              placeholder={t('teams.form.notesPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('common.actions.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? t('common.states.saving') : (isEdit ? t('common.actions.save') : t('common.actions.create'))}
          </button>
        </div>
      </div>
    </div>
  );
}
